import { ReactElement } from 'react';

import {
  Highlighted,
  RefereeDevice,
  RefereePlayer,
  Speaking,
  VideoStreamMode,
} from '@features/video/types';

import { PlayerActions, PlayerTile } from './PlayerTile';

export type Props = PlayerActions & {
  player: RefereePlayer;
  // горячие значения: кто говорит и над кем курсор; плитки подписываются сами
  speakingProxy?: Speaking;
  hoverProxy?: Highlighted;
  pinned?: boolean;
  // текст плитки игрока без устройств: на странице комнаты «Не подключен», в привате
  // «Ожидаем подключение»
  offlineText?: string;
  onPin?: () => void;
};

// Слот камеры по роли устройства: первая плитка — основная камера, вторая — дополнительная
const SLOT_INDEX: Record<VideoStreamMode, number> = { main: 0, extra: 1 };

// Плитки игрока по слотам камер, с ключами по слотам. Устройство с ролью занимает свой
// слот, без роли — первый свободный, лишние идут в конец; пустой слот — «Не подключена».
// Без устройств — плитка состояния в первом слоте. Удаленный — одна плитка состояния.
// Переведенный в приват без устройств — плитка состояния в каждом слоте: его место в сетке
// сохраняется. Ключ по слоту, а не по устройству: отключение, возврат, приват и удаление
// меняют состояние той же плитки на месте; страница комнаты анимирует появление и уход
// только при смене числа плиток
export function playerTiles({ player, ...rest }: Props): ReactElement[] {
  const { key: userId, devices, camerasRequired = 1 } = player;
  if (player.removed) return [<PlayerTile key={slotKey(userId, 0)} player={player} {...rest} />];
  if (player.inPrivateRoom && devices.length === 0) {
    return Array.from({ length: camerasRequired }, (_, i) => (
      <PlayerTile
        key={slotKey(userId, i)}
        player={player}
        index={i}
        count={camerasRequired}
        {...rest}
      />
    ));
  }

  const slots: (RefereeDevice | null)[] = Array.from({ length: camerasRequired }, () => null);
  const spare: RefereeDevice[] = [];
  devices.forEach((device) => {
    const i = device.slot ? SLOT_INDEX[device.slot] : -1;
    if (i >= 0 && i < slots.length && !slots[i]) slots[i] = device;
    else spare.push(device);
  });
  spare.forEach((device) => {
    const free = slots.indexOf(null);
    if (free >= 0) slots[free] = device;
    else slots.push(device);
  });

  const count = slots.length;
  return slots.map((device, i) =>
    device ? (
      <PlayerTile
        key={slotKey(userId, i)}
        player={player}
        device={device}
        index={i}
        count={count}
        {...rest}
      />
    ) : i === 0 && devices.length === 0 ? (
      <PlayerTile key={slotKey(userId, 0)} player={player} index={0} count={count} {...rest} />
    ) : (
      <PlayerTile
        key={slotKey(userId, i)}
        player={player}
        missing
        index={i}
        count={count}
        {...rest}
      />
    ),
  );
}

// Все плитки игрока подряд. Имя полностью только на первой
export function PlayerTiles(props: Props) {
  return <>{playerTiles(props)}</>;
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Ключ плитки: запись игрока и номер слота камеры
function slotKey(playerKey: string, slot: number): string {
  return `${playerKey}:slot-${slot}`;
}
